/**
 * 「听写历史」哪些任务组是**展开**的 —— 跨页面导航记住它（2026-10-29）。
 *
 * ## 用户原话（这一层的全部理由）
 *
 * > "比如，我点击「开始听写」、「看题目」，或者点击「再听一遍」、「去批改」。
 * >  点进去之后，再点击「回听写历史」按钮，回去之后，应该保持原来的展开状态 ——
 * >  也就是我原来点进去之前是怎么样展开的，回去之后就不需要再怎么展开。
 * >  现在点进去回到「听写历史」的话，它全部又是默认的收起状态了。
 * >  所以，只有第一次点进「听写历史」的时候，才需要默认是收起状态。"
 *
 * ## 为什么不用 URL 参数
 *
 * 展开状态要跟着「回听写历史」这条链接走，而那条链接在**三个页面**里各有一份
 * （历史页自己、批改页、播放器）。把 `?open=a,b` 传下去意味着：每加一个入口就要
 * 记得把它带上，漏一处就"莫名其妙又全收起了"。存储一份、按孩子读，入口就永远不用
 * 关心这件事。
 *
 * ## 为什么是 `sessionStorage` 而不是 `localStorage`
 *
 * "第一次点进"是**这一次浏览**的语义：换一台设备、关掉浏览器再打开，重新从
 * 全部收起开始是合理的（也避免家长几个月后发现某些组莫名其妙一直是展开的）。
 * 而在同一个标签页里点进点出、刷新，都应当保持。
 *
 * ⚠️ 存储可能不可用（隐私模式 / 配额满 / 被策略禁用）。两种失败都**静默降级**
 *    成"全部收起"：展开状态丢了不影响任何数据，绝不能因此抛错把页面打挂。
 */
const PREFIX = 'fht.dictationHistory.open.v2.';
/**
 * ⚠️ 键里的 `v2` 是**故意的**（2026-10-29）：第一版会在"刚布置完任务"时
 * 自动展开那一组并写进存储，于是用户手机上从此一直是展开的
 * （他的原话："默认任务要折叠呀……跟桌面端那样"）。
 * 自动展开已经去掉，但**旧键里那份"被自动展开"的状态还在** ——
 * 换个键等于一次性清掉它，让所有人从"全部折叠"重新开始。
 * 以后再改展开语义，也照这个办法升版本，不要去猜旧值是什么意思。
 */

/** 某个孩子的存储键（按孩子分开：换孩子不该继承上个孩子的展开状态）。 */
export function openGroupsStorageKey(studentId: string): string {
  return `${PREFIX}${studentId}`;
}

/** 读：拿不到 / 解析失败 / 不是数组 → 空集合（全部收起）。 */
export function loadOpenGroups(studentId: string): Set<string> {
  if (typeof window === 'undefined' || studentId === '') return new Set();
  try {
    const raw = window.sessionStorage.getItem(openGroupsStorageKey(studentId));
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((item): item is string => typeof item === 'string'));
  } catch {
    return new Set();
  }
}

/** 写：失败静默（见文件头）。 */
export function saveOpenGroups(studentId: string, keys: ReadonlySet<string>): void {
  if (typeof window === 'undefined' || studentId === '') return;
  try {
    window.sessionStorage.setItem(openGroupsStorageKey(studentId), JSON.stringify([...keys]));
  } catch {
    /* 隐私模式 / 配额满：展开状态不是关键路径，丢掉即可 */
  }
}

/** 切换一个组的开合（**不改原集合**，返回新集合 —— 方便 React 判等）。 */
export function toggleGroupKey(keys: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(keys);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** 确保某个组是展开的（布置完 / 新建会话后用来"把它亮出来"）。 */
export function withGroupOpen(keys: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(keys);
  next.add(key);
  return next;
}
